import { computed, ref, toRefs, watch } from "vue";
import { filtersState } from "./filtersState";

const indexMask = {
  decimal: ",",
  thousands: "",
  prefix: "",
  precision: 2,
  masked: false,
};

const toNumber = (value) => parseFloat(String(value).replace(",", "."));

export function useGeneticFilters(props) {
  const {
    zebu_ranking, ancp_top, geneplus_top,
    zebu_index_min, zebu_index_max, ancp_mgte_min, ancp_mgte_max,
    geneplus_iqg_min, geneplus_iqg_max, genetic_evaluation,
  } = toRefs(filtersState);

  const geneticIndexOptions = [
    { value: "zebu_index", label: "iABCZ", min: zebu_index_min, max: zebu_index_max,
      categoryLabel: "DECA", categoryState: zebu_ranking,
      categoryOptions: () => props.zebuRankingOptions },
    { value: "ancp_mgte", label: "ANCP (MGTe)", min: ancp_mgte_min, max: ancp_mgte_max,
      categoryLabel: "TOP", categoryState: ancp_top,
      categoryOptions: () => props.ancpTopOptions || [] },
    { value: "geneplus_iqg", label: "GenePlus (IQG)", min: geneplus_iqg_min, max: geneplus_iqg_max,
      categoryLabel: "TOP", categoryState: geneplus_top,
      categoryOptions: () => props.geneplusTopOptions || [] },
  ];

  const selectedGeneticIndex = genetic_evaluation;
  const selectedGeneticOption = computed(() =>
    geneticIndexOptions.find((option) => option.value === selectedGeneticIndex.value),
  );

  const geneticIndexes = ref({ min: "", max: "" });
  const showFlashMessage = ref(false);

  const categoricalLabel = computed(() => selectedGeneticOption.value?.categoryLabel ?? "");
  const categoricalOptions = computed(() => selectedGeneticOption.value?.categoryOptions() ?? []);
  const categoricalSelection = computed({
    get: () => selectedGeneticOption.value?.categoryState.value ?? [],
    set: (value) => {
      if (selectedGeneticOption.value) selectedGeneticOption.value.categoryState.value = value;
    },
  });
  const categoricalItemLabel = (option) =>
    selectedGeneticIndex.value === "zebu_index" ? option : `TOP ${option}`;

  const categoricalExpanded = ref(false);
  const visibleCategorical = computed(() =>
    categoricalExpanded.value ? categoricalOptions.value : categoricalOptions.value.slice(0, 4),
  );

  watch(categoricalSelection, () => {
    if (categoricalSelection.value.length > 4) categoricalExpanded.value = true;
  });

  const loadSelectedRangeInputs = () => {
    geneticIndexes.value.min = selectedGeneticOption.value?.min.value ?? "";
    geneticIndexes.value.max = selectedGeneticOption.value?.max.value ?? "";
  };

  const applyRangeFilter = () => {
    const { min, max } = geneticIndexes.value;

    if (min !== "" && max !== "" && toNumber(max) < toNumber(min)) {
      showFlashMessage.value = true;
      return false;
    }

    showFlashMessage.value = false;
    geneticIndexOptions.forEach((option) => {
      const isSelected = option.value === selectedGeneticIndex.value;
      option.min.value = isSelected ? min : "";
      option.max.value = isSelected ? max : "";
    });
    return true;
  };


  watch(selectedGeneticIndex, (selected) => {
    loadSelectedRangeInputs();

    geneticIndexOptions.forEach((option) => {
      if (option.value === selected) return;
      option.min.value = "";
      option.max.value = "";
      option.categoryState.value = [];
    });
  });

  watch(
    [zebu_index_min, zebu_index_max, ancp_mgte_min, ancp_mgte_max,
      geneplus_iqg_min, geneplus_iqg_max, zebu_ranking, ancp_top, geneplus_top],
    () => {
      const populated = geneticIndexOptions.find(
        (option) =>
          option.min.value !== "" ||
          option.max.value !== "" ||
          option.categoryState.value.length > 0,
      );
      selectedGeneticIndex.value = populated?.value ?? selectedGeneticIndex.value;
      loadSelectedRangeInputs();
    },
  );

  return {
    indexMask,
    geneticIndexOptions,
    selectedGeneticIndex,
    geneticIndexes,
    showFlashMessage,
    categoricalLabel,
    categoricalOptions,
    categoricalSelection,
    categoricalItemLabel,
    categoricalExpanded,
    visibleCategorical,
    applyRangeFilter,
  };
}
